import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type { ChangePasswordParams, ForgotPasswordParams, LoginCredentials, LoginResponseModel, MeResult, ResetPasswordParams, ValidateTokenParams } from '@/domain/models/auth.js';
import type { AuthRepository } from '@/domain/repositories/auth-repository.js';
import type {
    ChangePassword,
    FirstAccess,
    ForgotPassword,
    GetEntraOptions,
    GetMe,
    Login,
    Logout,
    RedeemEntraTicket,
    ResetPassword,
    ValidateResetToken
} from '@/domain/use-cases/auth.js';

export class LoginUseCase implements Login {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: LoginCredentials): Promise<Either<AbstractError, LoginResponseModel>> {
        try {
            return right(await this.repository.login(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LogoutUseCase implements Logout {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.logout();
            return right(undefined);
        } catch {
            return right(undefined);
        }
    }
}

export class GetMeUseCase implements GetMe {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(): Promise<Either<AbstractError, MeResult>> {
        try {
            return right(await this.repository.getMe());
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ForgotPasswordUseCase implements ForgotPassword {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: ForgotPasswordParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.forgotPassword(params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ResetPasswordUseCase implements ResetPassword {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: ResetPasswordParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.resetPassword(params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ValidateResetTokenUseCase implements ValidateResetToken {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: ValidateTokenParams): Promise<Either<AbstractError, boolean>> {
        try {
            return right(await this.repository.validateResetToken(params));
        } catch {
            return right(false);
        }
    }
}

export class FirstAccessUseCase implements FirstAccess {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: ResetPasswordParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.firstAccess(params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetEntraOptionsUseCase implements GetEntraOptions {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(): Promise<Either<AbstractError, { enabled: boolean }>> {
        try {
            return right(await this.repository.getEntraOptions());
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RedeemEntraTicketUseCase implements RedeemEntraTicket {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(ticket: string): Promise<Either<AbstractError, LoginResponseModel>> {
        try {
            return right(await this.repository.redeemEntraTicket(ticket));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ChangePasswordUseCase implements ChangePassword {
    constructor(private readonly repository: AuthRepository) {}

    public async execute(params: ChangePasswordParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.changePassword(params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
